import { useEffect } from 'react'
import { useTheme } from 'next-themes'
import { usePreference } from './usePreference'
import { chrome } from '../theme/tokens'

// Mounted ONCE (in AppProviders). Applies device-scoped preferences to the
// document element so recipes/global CSS pick them up. Renders nothing.
export function PreferenceEffects(): null {
  const [density] = usePreference('appearance.density')
  const [reduceMotion] = usePreference('appearance.reduceMotion')
  const [theme] = usePreference('appearance.theme')
  const { setTheme, resolvedTheme } = useTheme()

  // Density → [data-density]. Recipes gate this behind @media (pointer: fine),
  // so it is automatically inert on touch. Comfortable = attribute absent.
  useEffect(() => {
    const el = document.documentElement
    if (density === 'compact') el.dataset.density = 'compact'
    else delete el.dataset.density
  }, [density])

  // Reduce motion → [data-reduce-motion]. The global CSS rule keyed off this
  // attribute OR the OS media query means the toggle can only ADD restraint.
  useEffect(() => {
    const el = document.documentElement
    if (reduceMotion === true) el.dataset.reduceMotion = 'reduce'
    else delete el.dataset.reduceMotion
  }, [reduceMotion])

  // Theme → next-themes owns the .dark class + OS ('system') resolution + persistence
  // (shared storageKey pref:appearance.theme, set in AppProviders). Slice 7 activation.
  useEffect(() => {
    setTheme(theme as string)
  }, [theme, setTheme])

  // Mobile browser chrome matches the canvas per resolved mode. Hex is sourced
  // from the theme's canvas token (chrome.canvas) — the same values the semantic
  // layer paints the page background — rather than duplicated as raw literals.
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]')
    if (meta) {
      meta.setAttribute('content', resolvedTheme === 'dark' ? chrome.canvas.dark : chrome.canvas.light)
    }
  }, [resolvedTheme])

  return null
}
